import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { supabase } from '../../lib/supabase'
import { loadTenantForUser } from '../../hooks/useAuth'
import { useAuthStore } from '../../store/authStore'
import { logAuthEvent } from '../../lib/auditLog'
import toast from 'react-hot-toast'
import { Spinner } from '../../components/ui/Spinner'
import { Eye, EyeOff } from 'lucide-react'

const schema = z.object({
  email: z.string().email('Enter a valid email'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
})
type FormData = z.infer<typeof schema>


export default function LoginPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [showPass, setShowPass] = useState(false)
  const { setUser, setForcePasswordChange } = useAuthStore()

  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { email: searchParams.get('email') ?? '' },
  })

  async function onSubmit(values: FormData) {
    const timeoutId = setTimeout(() => {
      toast.error('Login timed out. Please check your connection and try again.')
    }, 15000)

    try {
      console.log('[Login] Attempting signInWithPassword...')
      const { data, error } = await supabase.auth.signInWithPassword({
        email: values.email,
        password: values.password,
      })

      if (error) {
        console.error('[Login] Supabase auth error:', error.message, error)
        toast.error(error.message)
        return
      }

      const authUser = data.user
      if (!authUser) {
        toast.error('Login failed. Please try again.')
        return
      }

      console.log('[Login] Auth success, user:', authUser.id)

      setUser({
        id: authUser.id,
        email: authUser.email ?? '',
        full_name: authUser.user_metadata?.full_name ?? authUser.email ?? '',
        avatar_url: authUser.user_metadata?.avatar_url,
      })
      const mustChangePassword = !!authUser.user_metadata?.force_password_change
      setForcePasswordChange(mustChangePassword)

      console.log('[Login] Loading tenant...')
      await loadTenantForUser(authUser.id)

      // Audit the sign-in (tenant is resolved now, so RLS accepts the row). Fire-and-
      // forget — the session persists, so it need not block navigation.
      void logAuthEvent(useAuthStore.getState().tenant?.id, authUser.id, 'login', 'Signed in')

      if (mustChangePassword) {
        toast('Please set a new password to continue.', { icon: '🔑' })
        navigate('/auth/change-password', { replace: true })
        return
      }

      toast.success('Welcome back!')
      navigate('/dashboard', { replace: true })
    } catch (err) {
      console.error('[Login] Unexpected error:', err)
      toast.error('Login failed. Please try again.')
    } finally {
      clearTimeout(timeoutId)
    }
  }

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-gradient-to-br from-brand-50 via-amber-50 to-orange-100">
      {/* LEFT — Hero */}
      <div className="flex-1 px-6 pt-8 pb-12 lg:px-12 lg:py-14 xl:px-20 flex flex-col relative overflow-hidden">
        {/* Decorative blobs */}
        <div aria-hidden className="absolute -top-24 -left-24 h-72 w-72 rounded-full bg-brand-200/40 blur-3xl" />
        <div aria-hidden className="absolute bottom-0 right-0 h-64 w-64 rounded-full bg-amber-200/50 blur-3xl" />

        <div className="relative flex items-center gap-2.5">
          <div className="h-10 w-10 rounded-xl bg-brand-500 flex items-center justify-center shadow-md">
            <span className="text-white font-bold text-lg">P</span>
          </div>
          <span className="font-extrabold text-xl text-gray-900">Palstro</span>
        </div>

        <div className="relative flex-1 flex flex-col lg:flex-row items-center gap-8 lg:gap-10 mt-10 lg:mt-0">
          {/* Picture — LEFT */}
          <div className="relative w-full lg:w-[62%] xl:w-[64%] mx-auto lg:mx-0">
            <div className="absolute top-2 -left-2 lg:-left-4 bg-white rounded-full shadow-card-hover px-3.5 py-1.5 text-xs font-bold text-sage-700 z-10 flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-sage-500 animate-pulse" />
              Live in 2 minutes
            </div>
            <img
              src="/login-hero.jpg"
              alt="African business team reviewing accounting charts in a modern office"
              loading="lazy"
              className="w-full h-auto rounded-2xl shadow-card-hover object-cover aspect-[4/3]"
            />
          </div>

          {/* Wording — RIGHT of the picture */}
          <div className="flex-1 min-w-0">
            <h1 className="text-3xl sm:text-4xl xl:text-5xl font-black tracking-tight text-gray-900 leading-[1.05]">
              A real ERP
              <br />
              for the price of
              <br />
              <span className="bg-gradient-to-r from-brand-500 via-amber-500 to-red-500 bg-clip-text text-transparent">
                a pizza.
              </span>
            </h1>

            <p className="mt-5 text-base sm:text-lg text-gray-700 max-w-md leading-relaxed">
              Full accounting, payroll, inventory &amp; POS — IFRS &amp; tax compliant, built for Nigerian businesses.
            </p>
          </div>
        </div>
      </div>

      {/* RIGHT — Auth */}
      <div className="lg:w-[440px] xl:w-[480px] bg-white lg:shadow-2xl px-6 py-10 lg:px-10 lg:py-14 flex flex-col justify-center">
        <div className="w-full max-w-sm mx-auto">
          <h2 className="text-3xl font-extrabold text-gray-900">Welcome back</h2>
          <p className="text-sm text-gray-500 mt-1.5">Sign in to your kitchen.</p>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 mt-8">
            <div>
              <label className="label">Email address</label>
              <input
                type="email"
                className="input"
                placeholder="you@company.com"
                autoFocus
                {...register('email')}
              />
              {errors.email && <p className="mt-1 text-xs text-red-500">{errors.email.message}</p>}
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="label mb-0">Password</label>
                <Link to="/forgot-password" className="text-xs text-brand-600 hover:underline">
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <input
                  type={showPass ? 'text' : 'password'}
                  className="input pr-10"
                  placeholder="••••••••"
                  {...register('password')}
                />
                <button
                  type="button"
                  onClick={() => setShowPass(!showPass)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPass ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {errors.password && <p className="mt-1 text-xs text-red-500">{errors.password.message}</p>}
            </div>

            <button type="submit" disabled={isSubmitting} className="btn-primary w-full mt-2 h-11">
              {isSubmitting ? <Spinner size="sm" /> : 'Sign in'}
            </button>
          </form>

          <p className="text-center text-sm text-gray-500 mt-6">
            New to Palstro?{' '}
            <Link to="/register" className="text-brand-600 font-semibold hover:underline">
              Start free
            </Link>
          </p>
        </div>
      </div>
    </div>
  )
}
